import { AuthenticationServiceProvider } from 'module/api/account/types';
import { TokenAuthentication } from 'module/authentication/token/service';
import { triggerOtp } from 'module/authentication/two-factor-authentication/utils';
import { PermissionType } from 'module/authentication/types';
import { getTokenMeta } from 'module/authentication/utils/decode-token';
import { useQueryParams } from 'module/helpers/location-query-params';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginWithTokenProps } from './types';

type Loading = boolean;
type Error = boolean;

export const useLoginWithToken = ({
  token,
  tokenType,
  oktaData,
  postLogin,
  postOtpTrigger,
}: LoginWithTokenProps): [Loading, Error] => {
  const history = useNavigate();
  const { oktaAuth } = useOktaAuth();
  const [isError, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const params = useQueryParams();
  const externalRefKey = params.get('externalRefKey') ?? '';
  const projectId = params.get('projectId') ?? '';
  const isCognitoEnabled =
    oktaData?.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;
  const clientId = oktaData?.clientId;

  useEffect(() => {
    const loginWithoutOtp = async () => {
      setLoading(true);
      if (TokenAuthentication.exists()) {
        await TokenAuthentication.clear();
      }
      if (clientId) {
        try {
          /**
           * Check if magic link contains hash to load the task drawer.
           */

          await TokenAuthentication.create(
            token,
            tokenType,
            clientId,
            externalRefKey,
            projectId,
          );

          oktaAuth.authStateManager.updateAuthState();
          setLoading(false);
          postLogin();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          setLoading(false);
          setError(true);
        }
      }
    };

    const loginWithOtp = async () => {
      try {
        /**
         * Check if magic link contains hash to load the task drawer.
         */

        if (TokenAuthentication.exists()) {
          postLogin();
        } else {
          triggerOtp(token);
          postOtpTrigger(token);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        setError(true);
      }
    };

    const exec = () => {
      const { decoded } = getTokenMeta(token);

      const isOTPEnabled =
        decoded?.permission === PermissionType.UserOtp || false;

      if (isOTPEnabled) {
        loginWithOtp();
      } else {
        loginWithoutOtp();
      }
    };

    exec();
  }, [
    token,
    oktaAuth,
    history,
    tokenType,
    externalRefKey,
    projectId,
    clientId,
    isCognitoEnabled,
    postLogin,
    postOtpTrigger,
  ]);

  return [isLoading, isError];
};
